import React, { useState, useEffect, useRef } from 'react';
import { LoadingSpinner } from './LoadingSpinner';

interface LabelLogosProps {
  name: string;
  url?: string;
}

export function LabelLogos({ name, url }: LabelLogosProps) {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [aspectRatio, setAspectRatio] = useState<number | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !url) return;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px 200px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [url]);

  useEffect(() => {
    if (!url || !shouldLoad) return;
    let isMounted = true;
    const img = new window.Image();
    img.decoding = 'async';
    img.onload = () => {
      if (!isMounted) return;
      if (img.naturalWidth && img.naturalHeight) {
        setAspectRatio(img.naturalWidth / img.naturalHeight);
      }
      setIsLoaded(true);
    };
    img.onerror = () => {
      if (!isMounted) return;
      setHasError(true);
    };
    img.src = url;

    return () => {
      isMounted = false;
    };
  }, [url, shouldLoad]);

  // If we had a failure, or no URL is provided, use typography fallback
  if (hasError || !url) {
    return (
      <div data-nosnippet aria-hidden="true" className="flex items-center justify-center px-8 md:px-12 transition-opacity">
        <span className="font-display font-black tracking-tighter uppercase text-[24px] md:text-[32px] lg:text-[40px] whitespace-nowrap text-black" style={{ fontStretch: 'condensed' }}>
          {name}
        </span>
      </div>
    );
  }

  // Custom sizing logic for logos that render too small natively
  const isTooSmall = [
    'mercury records',
    'roadrunner records',
    'a&m records',
    'atlantic records',
    'decca records',
    'warner music global'
  ].includes(name.toLowerCase());

  // Logos with backgrounds or inner white details that become solid black if brightness(0) is applied
  const needsContrastList = [
    'mercury records',
    'atlantic records',
    'roadrunner records',
    'verve',
    'warner music global',
    'a&m records',
    'bmg',
    'blue note records',
    'emi records',
    'rockstar games',
    'riot games',
    'fifa'
  ];
  
  const needsContrast = needsContrastList.includes(name.toLowerCase());

  return (
    <div ref={containerRef} data-nosnippet aria-hidden="true" className="flex items-center justify-center px-6 md:px-8 transition-opacity min-w-[120px] relative group h-full">
      {!isLoaded && <LoadingSpinner />}
      <span
        role="presentation"
        aria-hidden="true"
        style={{
          backgroundImage: isLoaded ? `url("${url}")` : undefined,
          aspectRatio: aspectRatio ? `${aspectRatio}` : '3 / 1',
        }}
        className={`block bg-contain bg-center bg-no-repeat label-logo-img ${needsContrast ? 'label-logo-contrast' : 'label-logo-normal'} ${isTooSmall ? 'h-10 md:h-[50px] mx-2' : 'h-8 md:h-[42px]'} w-auto max-w-[200px] md:max-w-[260px] pointer-events-none z-10 transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
}
